import { useState, useCallback } from "react";
import DealerGroupingClient from "../../clients/DealerGroupingClient";
import { selectDealerFromGroup } from "../utils/dealerGroupSelection";

/**
 * Fetches dealer group and selects the appropriate dealer. Can be used by class
 * components (e.g. NADealerApp) or by the useDealerGroup hook.
 *
 * @param {Object} user - NADealer (or compatible) instance
 * @param {string|null} salesCode - Current sales code (e.g. from sessionStorage)
 * @param {string|null} page - Current page (e.g. from sessionStorage)
 * @returns {Promise<{ data: Array, selectedDealer: Object|undefined }>}
 */
export async function fetchDealerGroup(user, salesCode, page) {
  const data = await DealerGroupingClient.getDealerGroup(user, salesCode, page);
  const selectedDealer = selectDealerFromGroup(data, user, salesCode);
  return { data, selectedDealer };
}

/**
 * Hook to fetch dealer group and resolve selected dealer. Use in functional
 * components (e.g. EcommDealerOnboardingApp). For class components, use
 * fetchDealerGroup directly.
 *
 * @param {Object} user - NADealer (or compatible) instance
 * @returns {{ getDealerGroup: (salesCode, page) => Promise<{ data, selectedDealer }>, isLoading: boolean, error: Error|null }}
 */
export function useDealerGroup(user) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const getDealerGroup = useCallback(
    (salesCode, page) => {
      if (!user) {
        return Promise.reject(new Error("user is required"));
      }
      setIsLoading(true);
      setError(null);
      return fetchDealerGroup(user, salesCode, page)
        .then((result) => {
          setIsLoading(false);
          return result;
        })
        .catch((err) => {
          setError(err);
          setIsLoading(false);
          return Promise.reject(err);
        });
    },
    [user]
  );

  return { getDealerGroup, isLoading, error };
}

export default useDealerGroup;
